import React, { useEffect, useState } from "react";
import { executeIntent } from "../../../utils/IntentExecutor";
import { useReadMemory } from "../../../generic/hooks/useReadMemory";
import MemoryType from "../../../../service/memory/enum/MemoryType";
import IntentType from "../../../../intentexecutor/utils/IntentType";
import ErrorNotificationComponent from "../../../../components/generic/error/ErrorNotificationComponent";
import { List, notification, Spin, Tooltip, Typography } from "antd";
import { getFullName } from "../../../careprovider/visits/utils";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { useSearchParams } from "react-router-dom";
import { strings } from "../../../utils/strings";
import { performPostRequest } from "../../../../service/network/NetworkService";
import { getApiUrl } from "../../../../service/network/UrlMapper";
import SearchInput from "../../../generic/elements/search/SearchInput";

const ExistingCareCircleMembers = ({
  existingMembers,
  addedMembersMap,
  setAddedMembersMap,
}) => {
  const userJson = useReadMemory(MemoryType.USER);
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addedMembers, setAddedMembers] = useState([]);
  const [loadingMemberId, setLoadingMemberId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchParams] = useSearchParams();
  const patientIdentifier = searchParams.get("identifier");

  const handleAddClick = async (member) => {
    setLoadingMemberId(member.userIdentifier);
    const payload = {
      data: {
        teamMemberIdentifier: member?.userIdentifier,
        patientIdentifier: patientIdentifier,
        organizationIdentifier: userJson?.organizationIdentifier,
        creatorIdentifier: userJson?.userIdentifier,
      },
    };

    const response = await executeIntent(IntentType.ADD_TEAM_MEMBER, payload);

    if (response?.status === 500) {
      ErrorNotificationComponent(new Error("Something went wrong"));
    } else {
      notification.success({
        message: strings.CARE_TEAM_MEMBER_ADDED_SUCCESS,
        placement: "bottomRight",
      });
      setAddedMembers((prev) => [...prev, member.userIdentifier]);
      setAddedMembersMap((prev) => ({
        ...prev,
        [member.userIdentifier]: response[0]?.membershipIdentifier,
      }));
    }
    setLoadingMemberId(null);
  };

  const handleRemoveClick = async (member) => {
    setLoadingMemberId(member.userIdentifier);
    const payload = {
      memberships: [
        {
          membershipIdentifier: addedMembersMap[member?.userIdentifier],
        },
      ],
      creatorIdentifier: userJson?.userIdentifier,
    };
    const response = await executeIntent(
      IntentType.DELETE_GROUP_MEMBERSHIP,
      payload
    );

    if (response?.status === 500) {
      ErrorNotificationComponent(new Error("Something went wrong"));
    } else {
      notification.success({
        message: strings.CARE_TEAM_MEMBER_DELETED_SUCCESS,
        placement: "bottomRight",
      });
      setAddedMembers((prev) =>
        prev.filter((id) => id !== member.userIdentifier)
      );
      setAddedMembersMap(({ [member.userIdentifier]: _, ...rest }) => rest);
    }
    setLoadingMemberId(null);
  };

  useEffect(() => {
    const getCareCircleMember = async () => {
      setIsLoading(true);
      try {
        const payload = {
          intent: IntentType.GET_TEAM_MEMBERS,
          payload: {
            organizationIdentifier: userJson?.organizationIdentifier,
            query: searchQuery,
            excludeIdentifiers: existingMembers.map(
              (item) => item.userIdentifier
            ),
          },
          context: {},
        };

        const result = await performPostRequest(
          getApiUrl()[IntentType.GET_TEAM_MEMBERS],
          JSON.stringify(payload)
        );
        const responseData = await result.json();

        setMembers(responseData);
      } catch (error) {
        ErrorNotificationComponent(error);
      } finally {
        setIsLoading(false);
      }
    };

    getCareCircleMember();
  }, [existingMembers, searchQuery, userJson?.organizationIdentifier]);

  return (
    <>
      <SearchInput
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        cssClass="searchFilterContainer organizationSearch"
        searchPlaceholder="Search Member"
      />
      <List
        itemLayout="horizontal"
        dataSource={members}
        loading={isLoading}
        renderItem={(member) => {
          const isAdded = addedMembers.includes(member.userIdentifier);
          const isButtonLoading = loadingMemberId === member.userIdentifier;
          return (
            <List.Item
              actions={[
                <button className="toggleButton">
                  {isButtonLoading ? (
                    <Spin size="small" />
                  ) : isAdded ? (
                    <Tooltip title="Remove" placement="bottom">
                      <RemoveIcon
                        className="serviceslist-remove-icon"
                        onClick={() => handleRemoveClick(member)}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Add" placement="bottom">
                      <AddIcon
                        className="serviceslist-add-icon"
                        onClick={() => handleAddClick(member)}
                      />
                    </Tooltip>
                  )}
                </button>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Typography.Title level={5}>
                    {getFullName(member?.givenName, member?.familyName)}
                  </Typography.Title>
                }
                description={member?.memberTitle}
              />
            </List.Item>
          );
        }}
      />
    </>
  );
};

export default ExistingCareCircleMembers;
